import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { BrandLogo } from '@/types'
import Reveal from '@/components/storefront/Reveal'
import SectionHeading from '@/components/storefront/SectionHeading'

/**
 * Brand logo strip modelled on the reference site's "Shop by Apparel"
 * section. Logos are admin-managed (admin > Brand Logos); the marks are
 * third-party trademarks shown at the client's explicit request.
 *
 * Unlike HeroCarousel, an empty-but-successful response renders nothing: an
 * admin who deactivated every logo asked for the section to disappear, so no
 * hardcoded fallback overrides that. The static list only covers API failure.
 */
const FALLBACK = ['adidas', 'nike', 'puma', 'new-balance', 'umbro', 'macron', 'kappa', 'hummel', 'reebok']

export default function BrandStrip() {
  const logos = useQuery({
    queryKey: ['storefront-brands'],
    queryFn: async () => (await api.get<{ data: BrandLogo[] }>('/storefront/brand-logos')).data.data,
  })

  const rows: Pick<BrandLogo, 'id' | 'name' | 'image_path'>[] = logos.isError
    ? FALLBACK.map((name, i) => ({ id: -1 - i, name, image_path: `/brands/${name}.png` }))
    : (logos.data ?? [])

  if (rows.length === 0) return null

  return (
    <section className="mx-auto w-full max-w-6xl px-5 py-16">
      <Reveal>
        <SectionHeading align="center" title="Shop by apparel" />
        {/* One wrapping row ordered by the admin's sort - nine logos break
            5 over 4 at this max width, matching the reference layout. */}
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-8 sm:gap-x-20">
          {rows.map((logo) => (
            <img
              key={logo.id}
              src={logo.image_path}
              alt={logo.name}
              className="h-14 w-auto object-contain sm:h-24"
            />
          ))}
        </div>
      </Reveal>
    </section>
  )
}
